import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentsStore } from '../store';
import { useDomains } from '@/hooks/useDomains';
import { useDomainFolders } from '@/hooks/useDomains';
import { useEmployees } from '@/features/employees/hooks';
import { Calendar, ChevronDown, SlidersHorizontal, X } from 'lucide-react';

export const DocumentFiltersAdvanced = () => {
  const { t } = useTranslation();
  const {
    selectedDomainUuid,
    selectedFolderUuid,
    selectedEmployeeUuid,
    dateFrom,
    dateTo,
    setSelectedDomainUuid,
    setSelectedFolderUuid,
    setSelectedEmployeeUuid,
    setDateFrom,
    setDateTo,
    resetFilters,
  } = useDocumentsStore();

  const { domains, isLoading: isLoadingDomains } = useDomains();
  const { folders, isLoading: isLoadingFolders } = useDomainFolders(
    selectedDomainUuid || ''
  );
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees(
    selectedFolderUuid || undefined,
    { limit: 1000 } // Get all employees for the folder
  );

  const employees = useMemo(() => employeesData?.data || [], [employeesData]);

  const hasActiveFilters = Boolean(
    selectedDomainUuid ||
      selectedFolderUuid ||
      selectedEmployeeUuid ||
      dateFrom ||
      dateTo
  );
  const [isOpen, setIsOpen] = useState(hasActiveFilters);

  useEffect(() => {
    if (hasActiveFilters) setIsOpen(true);
  }, [hasActiveFilters]);

  const handleDomainChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value || null;
      setSelectedDomainUuid(value);
    },
    [setSelectedDomainUuid]
  );

  const handleFolderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value || null;
      setSelectedFolderUuid(value);
    },
    [setSelectedFolderUuid]
  );

  const handleEmployeeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value || null;
      setSelectedEmployeeUuid(value);
    },
    [setSelectedEmployeeUuid]
  );

  const handleDateFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDateFrom(e.target.value || null);
    },
    [setDateFrom]
  );

  const handleDateToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDateTo(e.target.value || null);
    },
    [setDateTo]
  );

  const clearDateFrom = useCallback(() => {
    setDateFrom(null);
  }, [setDateFrom]);

  const clearDateTo = useCallback(() => {
    setDateTo(null);
  }, [setDateTo]);

  return (
    <div className="mb-6 rounded-xl border border-neutral-200 bg-neutral-50">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="document-filters-advanced"
        onClick={() => setIsOpen(open => !open)}
        className="focus-visible:ring-primary-600 flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold text-neutral-800 focus-visible:ring-2 focus-visible:outline-none"
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-neutral-500" />
          {t('documents.filters.advanced', 'Filtres avancés')}
          {hasActiveFilters && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              {t('documents.filters.active', 'Actifs')}
            </span>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          id="document-filters-advanced"
          className="border-t border-neutral-200 px-4 pt-4 pb-4"
        >
          {hasActiveFilters && (
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={resetFilters}
                className="text-primary-600 hover:text-primary-700 flex min-h-9 items-center gap-1 rounded-lg px-2 text-xs font-medium transition-colors"
              >
                <X className="h-3 w-3" />
                {t('common.reset', 'Réinitialiser')}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {/* Domain Filter */}
        <div>
          <label
            htmlFor="domain-filter"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            {t('documents.filters.domain', 'Domaine')}
          </label>
          <select
            id="domain-filter"
            value={selectedDomainUuid || ''}
            onChange={handleDomainChange}
            disabled={isLoadingDomains}
            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm focus:ring-2 disabled:bg-neutral-100 disabled:text-neutral-500"
          >
            <option value="">
              {t('documents.filters.allDomains', 'Tous les domaines')}
            </option>
            {domains.map(domain => (
              <option key={domain.uuid} value={domain.uuid}>
                {domain.name}
              </option>
            ))}
          </select>
        </div>

        {/* Folder Filter */}
        <div>
          <label
            htmlFor="folder-filter"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            {t('documents.filters.folder', 'Dossier')}
          </label>
          <select
            id="folder-filter"
            value={selectedFolderUuid || ''}
            onChange={handleFolderChange}
            disabled={!selectedDomainUuid || isLoadingFolders}
            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm focus:ring-2 disabled:bg-neutral-100 disabled:text-neutral-500"
          >
            <option value="">
              {t('documents.filters.allFolders', 'Tous les dossiers')}
            </option>
            {folders.map(folder => (
              <option key={folder.uuid} value={folder.uuid}>
                {folder.name}
              </option>
            ))}
          </select>
        </div>

        {/* Employee Filter */}
        <div>
          <label
            htmlFor="employee-filter"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            {t('documents.filters.employee', 'Employé(e)')}
          </label>
          <select
            id="employee-filter"
            value={selectedEmployeeUuid || ''}
            onChange={handleEmployeeChange}
            disabled={!selectedFolderUuid || isLoadingEmployees}
            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm focus:ring-2 disabled:bg-neutral-100 disabled:text-neutral-500"
          >
            <option value="">
              {t('documents.filters.allEmployees', 'Tous les employés')}
            </option>
            {employees.map(employee => (
              <option key={employee.uuid} value={employee.uuid}>
                {employee.nom} {employee.prenom} - {employee.matriculeSalarie}
              </option>
            ))}
          </select>
        </div>

        {/* Date From Filter */}
        <div>
          <label
            htmlFor="date-from-filter"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            {t('documents.filters.dateFrom', 'Date de début')}
          </label>
          <div className="relative">
            <Calendar className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              id="date-from-filter"
              type="date"
              value={dateFrom || ''}
              onChange={handleDateFromChange}
              className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white py-2 pr-8 pl-10 text-sm focus:ring-2"
            />
            {dateFrom && (
              <button
                onClick={clearDateFrom}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 hover:bg-neutral-100"
                aria-label={t('common.clear', 'Effacer')}
              >
                <X className="h-3 w-3 text-neutral-400" />
              </button>
            )}
          </div>
        </div>

        {/* Date To Filter */}
        <div>
          <label
            htmlFor="date-to-filter"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            {t('documents.filters.dateTo', 'Date de fin')}
          </label>
          <div className="relative">
            <Calendar className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              id="date-to-filter"
              type="date"
              value={dateTo || ''}
              onChange={handleDateToChange}
              min={dateFrom || undefined}
              className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white py-2 pr-8 pl-10 text-sm focus:ring-2"
            />
            {dateTo && (
              <button
                onClick={clearDateTo}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 hover:bg-neutral-100"
                aria-label={t('common.clear', 'Effacer')}
              >
                <X className="h-3 w-3 text-neutral-400" />
              </button>
            )}
          </div>
        </div>
          </div>
        </div>
      )}
    </div>
  );
};
